/**
 * DoAr Dimension Registry
 * Single source of truth for dimension-level UX config:
 * templates, prompts, inspector types, supported outputs.
 *
 * Colors/labels are sourced from constants/dimensions.js.
 * This file adds the creative-layer configuration on top.
 */

import { DIM_COLORS, DIMENSION_META } from '../../constants/dimensions';
import { CMD } from '../runtime/commandTypes';

// ── Template definitions per dimension ────────────────────────────────────────
// Templates with subtypes = two-step selection (card → chips → Build)
// Templates without subtypes = single-step (card → Build immediately)
// prompt field: string used as the final command sent to CommandRuntime

const REGISTRY = {
  '1D': {
    id:          '1D',
    label:       'Thought Space',
    badge:       'THINK',
    description: 'Capture ideas, write briefs, develop concepts, and reason through projects.',
    color:       DIM_COLORS['1D'],
    bg:          DIMENSION_META['1D'].bg,
    askPlaceholder:   'Ask TheDoAR to sharpen the idea…',
    buildPlaceholder: 'Build this into a visual direction…',
    commandCategories: [CMD.CREATE, CMD.EDIT, CMD.EXPLAIN, CMD.COMPARE, CMD.CONTINUE],
    buildTemplates: [
      {
        key: 'brief', label: 'Project Brief', icon: '≡',
        desc: 'Structured creative project overview',
        prompt: 'Write a detailed creative project brief for this project including goals, audience, vision, and key deliverables.',
      },
      {
        key: 'concept', label: 'Concept Note', icon: '◈',
        desc: 'Core idea with 5 directions',
        prompt: 'Generate 5 distinct concept directions for this project with a one-line summary for each.',
      },
      {
        key: 'research', label: 'Research Plan', icon: '⊟',
        desc: 'Structured investigation outline',
        prompt: 'Create a structured research agenda for this project — topics, methods, and key questions to answer.',
      },
      {
        key: 'prompt', label: 'Generation Prompt', icon: '▷',
        desc: 'Optimized prompt for AI generation',
        prompt: 'Write an optimized prompt for generating the key visual or spatial output for this project.',
      },
      {
        key: 'script', label: 'Script', icon: '≡',
        desc: 'Narration or presentation script',
        prompt: 'Write a narration script for a short presentation walkthrough of this project.',
      },
      {
        key: 'outline', label: 'Story Outline', icon: '◎',
        desc: 'Narrative structure for a story',
        prompt: 'Create a story outline for this project — structure, key scenes, and intended emotional arc.',
      },
    ],
    quickPrompts: [
      'Write a project brief',
      'Give me 5 concept directions',
      'Create a narrative arc',
      'Generate a research agenda',
    ],
    starterPrompts: [
      { label: 'Brief',    text: 'Write a creative brief for my project.' },
      { label: 'Concept',  text: 'Generate 5 unique concept directions.' },
      { label: 'Script',   text: 'Write a narration script for this project.' },
      { label: 'Research', text: 'Create a structured research agenda.' },
    ],
    inspectorTypes:   [],
    supportedOutputs: ['text', 'brief', 'script', 'concept'],
    primaryObjectTypes: [],
    suggestedActions: ['Summarize', 'Turn into brief', 'Create next steps', 'Expand this concept'],
  },

  '2D': {
    id:          '2D',
    label:       'Graphical Space',
    badge:       'IMAGE',
    description: 'Create visuals, diagrams, moodboards, layouts, and design boards.',
    color:       DIM_COLORS['2D'],
    bg:          DIMENSION_META['2D'].bg,
    askPlaceholder:   'Create, edit, or arrange a visual…',
    buildPlaceholder: 'Build a moodboard, diagram, layout, or image prompt…',
    commandCategories: [CMD.CREATE, CMD.EDIT, CMD.STYLE, CMD.RENDER, CMD.COMPARE],
    buildTemplates: [
      {
        key: 'moodboard', label: 'Moodboard', icon: '⊡',
        desc: 'Visual reference and mood collection',
        prompt: 'Describe a moodboard for this project — key visual references, textures, colors, and atmosphere.',
      },
      {
        key: 'visual_brief', label: 'Visual Brief', icon: '≡',
        desc: 'Creative direction and style guide',
        prompt: 'Write a visual brief for this project — design direction, style references, color palette, and typography.',
      },
      {
        key: 'poster', label: 'Poster', icon: '⬜',
        desc: 'Formatted poster concept',
        prompt: 'Design a minimal poster concept for this project — layout, headline, and visual hierarchy.',
      },
      {
        key: 'diagram', label: 'Diagram', icon: '⊞',
        desc: 'Relationship or process diagram',
        prompt: 'Create a diagram showing the key spatial or conceptual relationships in this project.',
      },
      {
        key: 'color_palette', label: 'Color Palette', icon: '◈',
        desc: 'Color and material palette',
        prompt: 'Suggest a color palette and material palette for this project — primary, secondary, accent, and surface colors.',
      },
      {
        key: 'layout', label: 'Layout', icon: '⊟',
        desc: 'Spatial layout grid',
        prompt: 'Design a layout for this space — zones, circulation, and spatial hierarchy.',
      },
    ],
    quickPrompts: [
      'Generate a visual concept',
      'Create a moodboard description',
      'Design a minimal poster',
      'Describe a hero image',
    ],
    starterPrompts: [
      { label: 'Visual',    text: 'Generate a visual concept for my project.' },
      { label: 'Moodboard', text: 'Create a moodboard with key visual references.' },
      { label: 'Poster',    text: 'Design a minimal poster for this project.' },
      { label: 'Layout',    text: 'Create a layout diagram for this space.' },
    ],
    inspectorTypes:   [],
    supportedOutputs: ['image', 'canvas', 'diagram'],
    primaryObjectTypes: [],
    suggestedActions: ['Make photoreal', 'Add labels', 'Change style', 'Export'],
  },

  '2.5D': {
    id:          '2.5D',
    label:       'Video Space',
    badge:       'MOTION',
    description: 'Create video clips, timelines, storyboards, and motion sequences.',
    color:       DIM_COLORS['2.5D'],
    bg:          DIMENSION_META['2.5D'].bg,
    askPlaceholder:   'Create, edit, or animate a video…',
    buildPlaceholder: 'Build a storyboard, timeline, shot, or motion prompt…',
    commandCategories: [CMD.CREATE, CMD.ANIMATE, CMD.EDIT, CMD.RENDER, CMD.CONTINUE],
    buildTemplates: [
      {
        key: 'orbit', label: 'Camera Orbit', icon: '◎',
        desc: '360° orbit around the model',
        prompt: 'Create a smooth 10-second camera orbit around the selected model.',
      },
      {
        key: 'walkthrough', label: 'Walkthrough', icon: '▷',
        desc: 'First-person walkthrough',
        prompt: 'Create a first-person walkthrough through this space.',
      },
      {
        key: 'storyboard', label: 'Storyboard', icon: '⊞',
        desc: '30-second shot sequence',
        prompt: 'Create a storyboard for a 30-second cinematic presentation of this project.',
      },
      {
        key: 'timelapse', label: 'Time Lapse', icon: '▶',
        desc: 'Accelerated time or progress',
        prompt: 'Create a time lapse video showing the design context or progression.',
      },
      {
        key: 'social', label: 'Social Clip', icon: '⬜',
        desc: '15-second vertical clip',
        prompt: 'Create a 15-second social media clip for this project with dynamic framing.',
      },
      {
        key: 'highlight', label: 'Highlight Reel', icon: '◈',
        desc: 'Best angles montage',
        prompt: 'Create a highlight reel featuring the best viewpoints of this project.',
      },
    ],
    quickPrompts: [
      'Describe a compelling opening',
      'Suggest motion transitions',
      'Write a storyboard sequence',
      'Give me pacing notes',
    ],
    starterPrompts: [
      { label: 'Orbit',       text: 'Create a dramatic camera orbit of the model.' },
      { label: 'Walkthrough', text: 'Generate a first-person walkthrough of this space.' },
      { label: 'Storyboard',  text: 'Write a storyboard for a 30-second clip.' },
      { label: 'Social',      text: 'Create a 15-second social media clip.' },
    ],
    inspectorTypes:   [],
    supportedOutputs: ['video', 'storyboard'],
    primaryObjectTypes: [],
    suggestedActions: ['Create orbit', 'Add captions', 'Extend duration', 'Export'],
  },

  '3D': {
    id:          '3D',
    label:       'Model Space',
    badge:       'MODEL',
    description: 'Create, edit, and compose parametric 3D models and objects.',
    color:       DIM_COLORS['3D'],
    bg:          DIMENSION_META['3D'].bg,
    askPlaceholder:   'Ask how to make this space feel more real…',
    buildPlaceholder: 'Add light, material, scale, atmosphere, or detail…',
    commandCategories: [CMD.CREATE, CMD.EDIT, CMD.TRANSFORM, CMD.STYLE, CMD.ANALYZE, CMD.CAPTURE, CMD.RENDER],
    buildTemplates: [
      {
        key: 'building', label: 'Building', icon: '⬜',
        desc: 'Commercial or civic building',
        subtypes: [
          { key: 'coffee_shop', label: 'Coffee Shop', prompt: 'Create a timber coffee shop with glass storefront and outdoor seating' },
          { key: 'restaurant',  label: 'Restaurant',  prompt: 'Create a restaurant with modern timber interior' },
          { key: 'office',      label: 'Office',       prompt: 'Create a steel and glass office building' },
          { key: 'museum',      label: 'Museum',       prompt: 'Create a museum with concrete facade and skylight' },
          { key: 'gallery',     label: 'Gallery',      prompt: 'Create an art gallery with white walls and timber floor' },
          { key: 'library',     label: 'Library',      prompt: 'Create a library with timber structure and open reading spaces' },
        ],
      },
      {
        key: 'tower', label: 'Tower', icon: '⬡',
        desc: 'High-rise parametric tower',
        subtypes: [
          { key: 'glass',    label: 'Glass Tower',    prompt: 'Create a 300m glass tower' },
          { key: 'gold',     label: 'Gold Tower',     prompt: 'Create a 250m gold facade tower' },
          { key: 'twisting', label: 'Twisting Tower', prompt: 'Create a 300m twisting glass tower with diagonal exoskeleton ribs' },
          { key: 'concrete', label: 'Concrete Tower', prompt: 'Create a 200m concrete brutalist tower' },
        ],
      },
      {
        key: 'house', label: 'House', icon: '⌂',
        desc: 'Residential building',
        subtypes: [
          { key: 'modern',   label: 'Modern',       prompt: 'Create a modern house with flat roof and large windows' },
          { key: 'timber',   label: 'Timber Frame', prompt: 'Create a warm timber frame house' },
          { key: 'concrete', label: 'Concrete',     prompt: 'Create a minimalist concrete house' },
          { key: 'glass',    label: 'Glass House',  prompt: 'Create a glass house with open plan and steel structure' },
        ],
      },
      {
        key: 'pavilion', label: 'Pavilion', icon: '⊓',
        desc: 'Open-air pavilion structure',
        subtypes: [
          { key: 'steel',    label: 'Steel',    prompt: 'Create a minimal steel pavilion with cantilevered roof' },
          { key: 'timber',   label: 'Timber',   prompt: 'Create a timber pavilion with exposed structure' },
          { key: 'concrete', label: 'Concrete', prompt: 'Create a concrete pavilion with sculptural form' },
        ],
      },
      {
        key: 'interior', label: 'Interior', icon: '▣',
        desc: 'Interior space',
        subtypes: [
          { key: 'living',  label: 'Living Room', prompt: 'Create a modern living room interior with timber and glass' },
          { key: 'office',  label: 'Office',      prompt: 'Create a minimal open-plan office interior' },
          { key: 'retail',  label: 'Retail',      prompt: 'Create a retail interior with timber shelving and polished concrete' },
          { key: 'studio',  label: 'Studio',      prompt: 'Create a studio apartment interior with multifunctional furniture' },
        ],
      },
      {
        key: 'urban', label: 'Urban Block', icon: '⊞',
        desc: 'Urban context block or landscape',
        subtypes: [
          { key: 'mixed',       label: 'Mixed Use',    prompt: 'Create a mixed-use urban block with retail ground floor and apartments above' },
          { key: 'residential', label: 'Residential',  prompt: 'Create a residential urban block with courtyard' },
          { key: 'landscape',   label: 'Landscape',    prompt: 'Create a landscape with trees, paths, and water feature' },
          { key: 'plaza',       label: 'Public Plaza',  prompt: 'Create a public plaza with seating, trees, and paved ground' },
        ],
      },
    ],
    quickPrompts: [
      'Describe a 3D spatial layout',
      'Suggest materials and lighting',
      'Give me a composition guide',
      'Structure the model hierarchy',
    ],
    starterPrompts: [
      { label: 'Architecture', text: 'Create a timber coffee shop with glass storefront.' },
      { label: 'Tower',        text: 'Create a 300m glass tower with diagonal ribs.' },
      { label: 'Interior',     text: 'Generate a concept for a studio apartment interior.' },
      { label: 'Urban',        text: 'Create a mixed-use urban block with landscaping.' },
    ],
    inspectorTypes: [
      'parametric_tower', 'parametric_building', 'parametric_house',
      'parametric_room', 'parametric_pavilion', 'urban_block', 'landscape_context',
    ],
    supportedOutputs: ['3d_model', 'scene'],
    primaryObjectTypes: ['parametric_tower', 'parametric_building'],
    suggestedActions: ['Make taller', 'Change material', 'Add context', 'Render this', 'Send to GeoSpace'],
  },

  '3.5D': {
    id:          '3.5D',
    label:       'GeoSpace',
    badge:       'PLACE',
    description: 'Place models on earth, design site context, and create geospatial scenes.',
    color:       DIM_COLORS['3.5D'],
    bg:          DIMENSION_META['3.5D'].bg,
    askPlaceholder:   'Create, place, analyze, or capture a site…',
    buildPlaceholder: 'Build a site plan, placed model, map story, or geo command…',
    commandCategories: [CMD.CREATE, CMD.PLACE, CMD.ANALYZE, CMD.CAPTURE, CMD.RENDER, CMD.PUBLISH],
    buildTemplates: [
      {
        key: 'site_plan', label: 'Site Plan', icon: '◎',
        desc: 'Site analysis and spatial design',
        prompt: 'Design a site plan for this location — program, circulation, and key spatial moves.',
      },
      {
        key: 'urban_ctx', label: 'Urban Context', icon: '⊞',
        desc: 'Contextual urban design model',
        prompt: 'Design a mixed-use urban development for this site with public space and activated ground floor.',
      },
      {
        key: 'landscape', label: 'Landscape', icon: '◈',
        desc: 'Landscape and terrain design',
        prompt: 'Create a landscape design for this site — planting, topography, and key outdoor spaces.',
      },
      {
        key: 'view_corridor', label: 'View Analysis', icon: '▷',
        desc: 'Key view corridors and sight lines',
        prompt: 'Analyze the key view corridors and sight lines from this location.',
      },
      {
        key: 'park', label: 'Public Space', icon: '⬜',
        desc: 'Park, plaza, or public realm',
        prompt: 'Plan a public space for this site — park, plaza, or waterfront with seating and trees.',
      },
      {
        key: 'map_story', label: 'Map Story', icon: '≡',
        desc: 'Narrated location walkthrough',
        prompt: 'Create a narrated map story for this location — key moments, movement, and spatial experience.',
      },
    ],
    quickPrompts: [
      'Design a mixed-use development on this site',
      'Plan a public waterfront park here',
      'Describe a transit-oriented development',
      'Design a rooftop community garden',
    ],
    starterPrompts: [
      { label: 'Architecture', text: 'Place a small café in this location with warm timber interiors.' },
      { label: 'Urban',        text: 'Create a public plaza with trees, seating, and evening lighting.' },
      { label: 'Landscape',    text: 'Design a waterfront park for this site.' },
      { label: 'Story',        text: 'Turn this location into a shareable walkthrough story.' },
    ],
    inspectorTypes: ['parametric_tower', 'parametric_building'],
    supportedOutputs: ['geospatial_scene', 'viewpoint', 'story'],
    primaryObjectTypes: ['parametric_tower', 'parametric_building'],
    suggestedActions: ['Place on site', 'Add context', 'Capture viewpoint', 'Render this', 'Create story'],
  },

  '4D': {
    id:          '4D',
    label:       'Immersive Space',
    badge:       'XR',
    description: 'Design immersive AR and VR spatial experiences.',
    color:       DIM_COLORS['4D'],
    bg:          DIMENSION_META['4D'].bg,
    askPlaceholder:   'Design, describe, or navigate an XR experience…',
    buildPlaceholder: 'Build an immersive scene, interaction, or XR sequence…',
    commandCategories: [CMD.CREATE, CMD.ANIMATE, CMD.PLACE, CMD.PUBLISH],
    buildTemplates: [
      {
        key: 'xr_scene', label: 'XR Scene', icon: '◎',
        desc: 'Immersive spatial environment',
        prompt: 'Design an immersive XR scene for this project — spatial layout, anchors, and presence cues.',
      },
      {
        key: 'ar_overlay', label: 'AR Overlay', icon: '⊡',
        desc: 'Augmented reality overlay',
        prompt: 'Describe an AR overlay experience for this physical location — what appears and how.',
      },
    ],
    quickPrompts: [
      'Design an immersive AR experience',
      'Describe a 360° spatial story',
      'Give me an XR concept',
    ],
    starterPrompts: [
      { label: 'XR',     text: 'Design an immersive XR experience for this project.' },
      { label: 'AR',     text: 'Describe an AR overlay for this physical location.' },
    ],
    inspectorTypes: [],
    supportedOutputs: ['xr_scene'],
    primaryObjectTypes: [],
    suggestedActions: ['Add interaction', 'Preview in XR', 'Export scene'],
  },
};

// ── Alias maps ────────────────────────────────────────────────────────────────
// Canonical ID → URL slug
const DIM_TO_ROUTE = {
  '1D':   'thought',
  '2D':   'image',
  '2.5D': 'motion',
  '3D':   'model',
  '3.5D': 'geospace',
  '4D':   'xr',
};

// URL slug / alias → canonical ID (case-insensitive)
const ROUTE_ALIASES = {
  thought: '1D', notes: '1D', '1d': '1D',
  image:   '2D', graphical: '2D', canvas2d: '2D', '2d': '2D',
  motion:  '2.5D', video: '2.5D', '2.5d': '2.5D',
  model:   '3D', '3d': '3D', model3d: '3D',
  geospace: '3.5D', geo: '3.5D', place: '3.5D', '3.5d': '3.5D',
  xr:      '4D', '4d': '4D', immersive: '4D',
};

const VALID_IDS = new Set(['1D', '2D', '2.5D', '3D', '3.5D', '4D']);

// ── Helpers ───────────────────────────────────────────────────────────────────

export function getDimensionConfig(dim) {
  return REGISTRY[dim] || REGISTRY['1D'];
}

/** Accepts canonical IDs, URL slugs, or aliases. Returns canonical ID or null. */
export function normalizeDimension(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (VALID_IDS.has(s)) return s;
  return ROUTE_ALIASES[s.toLowerCase()] || null;
}

/** Canonical ID → friendly URL slug (e.g. '3D' → 'model') */
export function dimensionToRoute(dim) {
  return DIM_TO_ROUTE[dim] || dim?.toLowerCase() || 'model';
}

/** Canonical ID → backend string (same as ID for now) */
export function dimensionToBackend(dim) {
  return normalizeDimension(dim) || dim;
}

/** Canonical ID → display label */
export function dimensionToLabel(dim) {
  return getDimensionConfig(dim)?.label || dim;
}

/**
 * Given a free-text intent string, return the canonical dimension ID
 * that best matches (mirrors the frontend onboarding classifier).
 */
export function getDefaultDimensionForIntent(intent) {
  if (!intent) return '3D';
  const t = intent.toLowerCase();
  if (/video|animation|animate|walkthrough|orbit|reel|motion|film/.test(t)) return '2.5D';
  if (/site|location|map|geo|place|harbour|harbor|geospatial|neighbourhood/.test(t)) return '3.5D';
  if (/moodboard|mood board|image|visual|render|poster|diagram|graphic/.test(t)) return '2D';
  if (/brief|write|plan|script|notes|concept note|narrative|document/.test(t)) return '1D';
  return '3D';
}

export function isModelLikeDimension(dim) {
  return dim === '3D' || dim === '3.5D';
}

export function isGeoDimension(dim) {
  return dim === '3.5D';
}

export function isCanvasDimension(dim) {
  return dim === '2D';
}

export function isMotionDimension(dim) {
  return dim === '2.5D';
}

export function isThoughtDimension(dim) {
  return dim === '1D';
}

export { REGISTRY as DIMENSION_REGISTRY };
